import React, { useEffect, useState } from 'react';
import moment from 'moment';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Accordion,
  AccordionDetails,
  Box,
  TextField,
  Backdrop,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Grid,
} from '@mui/material';

import Table from './Table/Table';
import FilterActionBar from './FilterActionBar/FilterActionBar';
// import Legend from './Legend';

const columnMap = {
  deltaRadToastSales: 'RAD vs Toast Sales',
  deltaRadDayforceSales: 'RAD vs DF Sales',
  deltaRadExpectedTips: 'RAD vs Expected Tips',
  deltaDayforceExpectedTips: 'DF vs Expected Tips',
  deltaRadDayforceLabor: 'RAD vs DF Labor',
  storeId: 'Store ID',
  date: 'Date',
  toastSales: <>Toast Sales</>,
  dayforceSales: <>DF Sales</>,
  radSales: <>RAD Sales</>,
  toastTips: <>Toast Tips</>,
  oloTips: <>Olo Tips</>,
  radTips: <>RAD Tips</>,
  dfTips: <>DF Tips</>,
  dfLabor: <>DF Labor</>,
  radLabor: <>RAD Labor</>,
};

const Content = ({
  firebaseUser,
  role,
  formData,
  selectedOloStore,
  setSelectedOloStore,
  stores,
}) => {
  const yesterday = moment().subtract(1, 'days').toDate();

  const [rows, setRows] = useState([]);
  const [rowTotalCount, setRowTotalCount] = useState(0);
  const [rowPage, setRowPage] = useState(0);
  const [rowRowsPerPage, setRowRowsPerPage] = useState(10);
  const [rowOrder, setRowOrder] = useState('asc');
  const [rowOrderBy, setRowOrderBy] = useState('storeId');
  const [startDate, setStartDate] = useState(yesterday);
  const [endDate, setEndDate] = useState(yesterday);
  const [loading, setLoading] = useState(false);

  // New state for region filtering:
  const [selectedRegion, setSelectedRegion] = useState('All');

  // New state for the highlight filter (legend filter)
  const [highlightFilter, setHighlightFilter] = useState('');

  const [filterSignificantOnly, setFilterSignificantOnly] = useState(true);

  const [thresholds, setThresholds] = useState({
    sales: 0.05,
    tips: 0.05,
    labor: 0.05,
  });

  useEffect(() => {
    if (firebaseUser.getIdToken && formData) {
      handleGetData();
    }
  }, [
    selectedOloStore,
    selectedRegion, // re-run if region changes
    firebaseUser,
    rowPage,
    rowRowsPerPage,
    rowOrder,
    rowOrderBy,
    highlightFilter,
  ]);

  const handleGetData = async () => {
    try {
      setLoading(true);
      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      const computedStoreId =
        selectedRegion && selectedRegion !== 'All'
          ? stores
              .filter((store) => store.region === selectedRegion)
              .map((store) => store.storeId)
              .join(',')
          : selectedOloStore;

      const formattedStartDate = startDate
        ? moment(startDate).format('YYYYMMDD')
        : '';
      const formattedEndDate = endDate
        ? moment(endDate).format('YYYYMMDD')
        : '';

      const params = new URLSearchParams();
      params.append('storeId', computedStoreId);
      params.append('sortBy', rowOrderBy);
      params.append('order', rowOrder);
      if (formattedStartDate && formattedEndDate) {
        params.append('startDate', formattedStartDate);
        params.append('endDate', formattedEndDate);
      }
      if (highlightFilter) {
        params.append('highlightFilter', highlightFilter);
      }
      const queryString = `/hd-api/dip?${params.toString()}`;

      const response = await axios.get(queryString, {
        headers: {
          Authorization: `Bearer ${firebaseUserIdToken}`,
        },
        'Content-Type': 'application/json',
      });

      setRows(response.data.rows);
      setRowTotalCount(response.data.totalCount);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    // Optionally, reset searchField if desired.
  };

  const handleChangeSelectedStore = (e) => {
    setSelectedOloStore(e.target.value);
  };

  // New handler for region change
  const handleChangeSelectedRegion = (e) => {
    setSelectedRegion(e.target.value);
  };

  const handleSearch = async () => handleGetData();

  const handleRequestSort = (event, property) => {
    const isAsc = rowOrderBy === property && rowOrder === 'asc';
    setRowOrder(isAsc ? 'desc' : 'asc');
    setRowOrderBy(property);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (orderBy === 'timeWanted' || orderBy === 'timePlaced') {
      return new Date(b[orderBy]) - new Date(a[orderBy]);
    }
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const stableSort = (array, comparator) => {
    if (array?.length > 0) {
      const stabilizedThis = array.map((el, index) => [el, index]);
      stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
      });
      return stabilizedThis.map((el) => el[0]);
    } else {
      return [];
    }
  };

  const handleChangerowPage = (event, newPage) => {
    setRowPage(newPage);
  };

  const handleChangerowRowsPerPage = (event) => {
    setRowRowsPerPage(parseInt(event.target.value, 10));
    setRowPage(0);
  };

  // legendFilter should be one of "noOrders90days", "noOrders60days", "noOrders30days", or empty to clear.
  const handleLegendFilter = (legendFilter) => {
    // Toggle off if clicking the same active filter.
    setHighlightFilter((prev) => (prev === legendFilter ? '' : legendFilter));
  };

  let showStoreFilterFlag = false;
  if (
    role === 'DO' ||
    role === 'Basecamp' ||
    (role === 'Direct' && !formData.selectedStore)
  ) {
    showStoreFilterFlag = true;
  }
  const searchInput = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {/* <Typography mt={1} ml={2} variant='subtitle2'>
        Last Order Date:
      </Typography> */}
      <DatePicker
        selected={startDate}
        onChange={(date) => setStartDate(date)}
        selectsStart
        startDate={startDate}
        endDate={endDate}
        placeholderText='Start Date'
        customInput={
          <TextField
            size='small'
            style={{ backgroundColor: 'white', marginTop: 8 }}
          />
        }
      />
      <DatePicker
        selected={endDate}
        onChange={(date) => setEndDate(date)}
        selectsEnd
        startDate={startDate}
        endDate={endDate}
        minDate={startDate}
        placeholderText='End Date'
        customInput={
          <TextField
            size='small'
            style={{ backgroundColor: 'white', marginTop: 8 }}
          />
        }
      />
    </div>
  );

  return (
    <div>
      {/* <div style={{ width: '100%' }}>
        <Legend
          onLegendClick={handleLegendFilter}
          activeFilter={highlightFilter}
        />
      </div> */}
      <Grid container alignItems='center' spacing={2} mb={2} mt={2}>
        {/* Empty spacer */}
        <Grid item xs={4} />

        {/* Threshold Inputs in center */}
        <Grid item xs={4}>
          <Box display='flex' justifyContent='center' gap={2}>
            <TextField
              label='Sales Threshold %'
              type='number'
              value={thresholds.sales * 100}
              onChange={(e) =>
                setThresholds((prev) => ({
                  ...prev,
                  sales: parseFloat(e.target.value) / 100,
                }))
              }
              size='small'
              style={{ width: 130 }}
              InputProps={{ inputProps: { min: 0, max: 100, step: 0.1 } }}
            />
            <TextField
              label='Tips Threshold %'
              type='number'
              value={thresholds.tips * 100}
              onChange={(e) =>
                setThresholds((prev) => ({
                  ...prev,
                  tips: parseFloat(e.target.value) / 100,
                }))
              }
              size='small'
              style={{ width: 130 }}
              InputProps={{ inputProps: { min: 0, max: 100, step: 0.1 } }}
            />
            <TextField
              label='Labor Threshold %'
              type='number'
              value={thresholds.labor * 100}
              onChange={(e) =>
                setThresholds((prev) => ({
                  ...prev,
                  labor: parseFloat(e.target.value) / 100,
                }))
              }
              size='small'
              style={{ width: 130 }}
              InputProps={{ inputProps: { min: 0, max: 100, step: 0.1 } }}
            />
          </Box>
        </Grid>

        {/* Checkbox on right */}
        <Grid item xs={4}>
          <Box display='flex' justifyContent='flex-start'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterSignificantOnly}
                  onChange={(e) => setFilterSignificantOnly(e.target.checked)}
                />
              }
              label='Only show discrepancies'
            />
          </Box>
        </Grid>
      </Grid>

      <FilterActionBar
        firebaseUser={firebaseUser}
        handleKeyPress={handleKeyPress}
        searchInput={searchInput}
        handleSearch={handleSearch}
        handleClear={handleClear}
        showStoreFilter={showStoreFilterFlag}
        selectedOloStore={selectedOloStore}
        handleChangeSelectedStore={handleChangeSelectedStore}
        stores={stores}
        highlightFilter={highlightFilter}
        selectedRegion={selectedRegion}
        handleChangeSelectedRegion={handleChangeSelectedRegion}
        showRegionFilter={true}
        setLoading={setLoading}
      />
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 0,
        }}
      >
        <Accordion
          defaultExpanded
          style={{
            width: '100%',
            backgroundColor: '#f8f8f8',
            marginBottom: 0,
          }}
        >
          <AccordionDetails>
            <Table
              firebaseUser={firebaseUser}
              order={rowOrder}
              totalCount={rowTotalCount}
              orderBy={rowOrderBy}
              handleRequestSort={(event, property) =>
                handleRequestSort(event, property, 'row')
              }
              onPageChange={handleChangerowPage}
              onRowsPerPageChange={handleChangerowRowsPerPage}
              rowsPerPage={rowRowsPerPage}
              getComparator={getComparator}
              stableSort={stableSort}
              page={rowPage}
              columnMap={columnMap}
              rows={rows}
              setLoading={setLoading}
              thresholds={thresholds}
              filterSignificantOnly={filterSignificantOnly}
            />
          </AccordionDetails>
        </Accordion>
      </Box>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
    </div>
  );
};

export default Content;
